/**
 * ColumnPicker component for managing visible columns
 * Based on column sort/filter system documentation
 */
import React, { useState, useMemo } from 'react';
import {
  Popover, Input, Checkbox, Button, Space, Typography, Divider, Tag
} from 'antd';
import {
  SettingOutlined, SearchOutlined, CheckOutlined, CloseOutlined
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * ColumnPicker - Popover for selecting visible columns
 * 
 * @param {Object} props
 * @param {Array} props.columns - Array of { id, label, visible }
 * @param {Function} props.onChange - Handler for column visibility changes
 */
function ColumnPicker({ columns = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Separate columns into selected and hidden
  const { selectedColumns, hiddenColumns } = useMemo(() => {
    const selected = columns.filter((col) => col.visible);
    const hidden = columns.filter((col) => !col.visible);
    return { selectedColumns: selected, hiddenColumns: hidden };
  }, [columns]);

  // Filter by search
  const filteredSelected = useMemo(() => {
    if (!search) return selectedColumns;
    return selectedColumns.filter((col) =>
      col.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [selectedColumns, search]);

  const filteredHidden = useMemo(() => {
    if (!search) return hiddenColumns;
    return hiddenColumns.filter((col) =>
      col.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [hiddenColumns, search]);

  // Toggle column visibility
  const toggleColumn = (columnId) => {
    const newColumns = columns.map((col) =>
      col.id === columnId ? { ...col, visible: !col.visible } : col
    );
    onChange(newColumns);
  };

  // Show all columns
  const showAll = () => {
    const newColumns = columns.map((col) => ({ ...col, visible: true }));
    onChange(newColumns);
  };

  // Hide all columns (keep at least one)
  const hideAll = () => {
    if (columns.length === 0) return;
    const newColumns = columns.map((col, index) => ({
      ...col,
      visible: index === 0, // Keep first column visible
    }));
    onChange(newColumns);
  };

  // Count active columns
  const activeCount = columns.filter((col) => col.visible).length;

  // Popover content
  const content = (
    <div style={{ width: 280 }}>
      {/* Search */}
      <Input
        placeholder="Поиск столбцов..."
        prefix={<SearchOutlined />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 12 }}
      />

      {/* Actions */}
      <Space style={{ marginBottom: 12 }}>
        <Button size="small" icon={<CheckOutlined />} onClick={showAll}>
          Показать все
        </Button>
        <Button size="small" icon={<CloseOutlined />} onClick={hideAll}>
          Скрыть все
        </Button>
      </Space>

      <Divider style={{ margin: '8px 0' }} />

      {/* Selected columns */}
      {filteredSelected.length > 0 && (
        <>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Выбранные ({selectedColumns.length})
          </Text>
          <div style={{ marginTop: 4 }}>
            {filteredSelected.map((col) => (
              <div
                key={col.id}
                style={{
                  padding: '4px 0',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Checkbox
                  checked={true}
                  onChange={() => toggleColumn(col.id)}
                >
                  {col.label}
                </Checkbox>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Hidden columns */}
      {filteredHidden.length > 0 && (
        <>
          <Text type="secondary" style={{ fontSize: 12, marginTop: 12, display: 'block' }}>
            Скрытые ({hiddenColumns.length})
          </Text>
          <div style={{ marginTop: 4 }}>
            {filteredHidden.map((col) => (
              <div
                key={col.id}
                style={{
                  padding: '4px 0',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Checkbox
                  checked={false}
                  onChange={() => toggleColumn(col.id)}
                >
                  <Text type="secondary">{col.label}</Text>
                </Checkbox>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Empty state */}
      {filteredSelected.length === 0 && filteredHidden.length === 0 && (
        <Text type="secondary">Нет столбцов</Text>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      <Button icon={<SettingOutlined />}>
        Столбцы {activeCount > 0 && <Tag color="blue">{activeCount}</Tag>}
      </Button>
    </Popover>
  );
}

export default ColumnPicker;